/**
 * Leaderboard Page - Displays top players by ELO, wins, or level
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getRankColor, getRankBgColor } from '../../state/playerStatsStore';
import { shortKey } from '../../utils/format';
import { LeaderboardSkeleton, StatsSkeleton } from '../components/skeletons';
import { useLeaderboard, usePlayerStats, LeaderboardType } from '../../api/leaderboard';

// Format last played time
function formatLastPlayed(lastPlayedAt: string | null): string {
  if (!lastPlayedAt) return 'Never';
  try {
    const date = new Date(lastPlayedAt);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return 'Unknown';
  }
}

// Get current season info
function getCurrentSeasonInfo(): { name: string; daysRemaining: number } {
  const now = new Date();
  const seasonEnd = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59));
  const daysRemaining = Math.ceil((seasonEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return {
    name: `Season ${now.getUTCMonth() + 1}`,
    daysRemaining,
  };
}

export const LeaderboardPage: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const [activeTab, setActiveTab] = useState<LeaderboardType>('elo');

  // Use React Query for automatic caching, refetching, and fresh data on mount
  const { data: leaderboard = [], isLoading: leaderboardLoading } = useLeaderboard(activeTab);
  const { data: stats, isLoading: statsLoading } = usePlayerStats(wallet);

  const tabs: { key: LeaderboardType; label: string }[] = [
    { key: 'elo', label: 'ELO Rating' },
    { key: 'wins', label: 'Total Wins' },
    { key: 'level', label: 'Level' },
  ];

  const seasonInfo = getCurrentSeasonInfo();

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8 text-center"
      >
        <div className="mb-2 flex items-center justify-center gap-3">
          <h1 className="neon-text font-display text-3xl font-bold">LEADERBOARD</h1>
          <span className="rounded-full border border-arcade-pink/50 bg-arcade-pink/20 px-3 py-1 font-display text-xs text-arcade-pink">
            {seasonInfo.name.toUpperCase()}
          </span>
        </div>
        <p className="font-arcade text-sm text-white/60">
          Compete with players worldwide and climb the ranks
          <span className="ml-2 text-arcade-cyan">• {seasonInfo.daysRemaining}d remaining</span>
        </p>
      </motion.div>

      {/* Your Rank Card */}
      {wallet && statsLoading && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="mb-6"
        >
          <StatsSkeleton />
        </motion.div>
      )}
      {stats && !statsLoading && (
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`arcade-panel pixel-corners mb-6 rounded-lg border p-4 ${getRankBgColor(stats.rankTier)}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="font-pixel text-xs text-white/60">YOUR RANK</div>
              <div className={`font-display text-2xl ${getRankColor(stats.rankTier)}`}>
                {stats.rankName}
              </div>
              <div className="font-pixel text-xs text-white/50">#{stats.globalRank}</div>
            </div>
            <div className="text-right">
              <div className="font-pixel text-xs text-white/60">ELO RATING</div>
              <div className="neon-text font-display text-2xl">{stats.eloRating}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4 font-pixel text-xs">
            <div>
              <div className="text-white/50">Level</div>
              <div className="text-arcade-cyan">{stats.level}</div>
            </div>
            <div>
              <div className="text-white/50">Wins</div>
              <div className="text-arcade-green">{stats.totalWins}</div>
            </div>
            <div>
              <div className="text-white/50">Games</div>
              <div className="text-arcade-yellow">{stats.totalGames}</div>
            </div>
            <div>
              <div className="text-white/50">Best Streak</div>
              <div className="text-arcade-pink">{stats.bestStreak}</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Tab Selector */}
      <div className="mb-6 flex justify-center gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`arcade-panel rounded-lg px-3 py-2 sm:px-4 font-pixel text-sm transition-all ${
              activeTab === tab.key
                ? 'border-arcade-cyan bg-arcade-cyan/20 text-arcade-cyan'
                : 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Leaderboard Table */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="arcade-panel pixel-corners overflow-hidden rounded-lg"
      >
        {/* Header */}
        <div className="grid grid-cols-10 gap-2 border-b border-white/20 bg-black/30 px-2 py-3 font-pixel text-xs text-white/60 md:grid-cols-12 md:px-4">
          <div className="col-span-1 text-center">#</div>
          <div className="col-span-4 md:col-span-5">Player</div>
          <div className="col-span-2 text-center">
            {activeTab === 'elo' ? 'ELO' : activeTab === 'wins' ? 'Wins' : 'Level'}
          </div>
          <div className="col-span-3 text-center md:col-span-2">W/L</div>
          <div className="hidden text-center md:block md:col-span-2">Active</div>
        </div>

        {/* Loading State */}
        {leaderboardLoading && <LeaderboardSkeleton rows={10} />}

        {/* Empty State */}
        {!leaderboardLoading && leaderboard.length === 0 && (
          <div className="py-12 text-center">
            <div className="mb-2 text-4xl"></div>
            <div className="font-arcade text-white/60">No players on the leaderboard yet</div>
            <Link
              to="/matchmaking"
              className="mt-4 inline-block rounded-lg bg-arcade-cyan/20 px-4 py-2 font-pixel text-sm text-arcade-cyan transition-all hover:bg-arcade-cyan/30"
            >
              Be the first to play!
            </Link>
          </div>
        )}

        {/* Leaderboard Entries */}
        {!leaderboardLoading &&
          leaderboard.map((entry, index) => {
            const isCurrentPlayer = wallet && entry.wallet === wallet;
            const isTopThree = index < 3;

            return (
              <div
                key={entry.wallet}
                className={`grid grid-cols-10 items-center gap-2 border-b border-white/10 px-2 py-3 transition-colors md:grid-cols-12 md:px-4 ${
                  isCurrentPlayer
                    ? 'bg-arcade-cyan/10'
                    : isTopThree
                      ? 'bg-white/5'
                      : 'hover:bg-white/5'
                }`}
              >
                {/* Rank */}
                <div className="col-span-1 text-center">
                  {isTopThree ? (
                    <span
                      className={`font-display text-sm font-bold ${index === 0 ? 'text-yellow-400' : index === 1 ? 'text-slate-300' : 'text-amber-600'}`}
                    >
                      {index + 1}
                    </span>
                  ) : (
                    <span className="font-pixel text-white/60">{entry.rank}</span>
                  )}
                </div>

                {/* Player */}
                <div className="col-span-4 md:col-span-5">
                  <div
                    className={`truncate font-arcade text-sm ${isCurrentPlayer ? 'text-arcade-cyan' : 'text-white'}`}
                  >
                    {isCurrentPlayer ? 'You' : entry.displayName || shortKey(entry.wallet)}
                  </div>
                  <div className={`font-pixel text-xs ${getRankColor(entry.rankTier)}`}>
                    {entry.rankName}
                  </div>
                </div>

                {/* Main Stat */}
                <div className="col-span-2 text-center">
                  <span className="neon-text font-display text-lg">
                    {activeTab === 'elo'
                      ? entry.eloRating
                      : activeTab === 'wins'
                        ? entry.totalWins
                        : entry.level}
                  </span>
                </div>

                {/* Win/Loss */}
                <div className="col-span-3 text-center font-pixel text-xs md:col-span-2">
                  <span className="text-arcade-green">{entry.totalWins}</span>
                  <span className="text-white/30">/</span>
                  <span className="text-arcade-pink">{entry.totalGames - entry.totalWins}</span>
                </div>

                {/* Last Active */}
                <div className="hidden text-center font-pixel text-xs text-white/50 md:block md:col-span-2">
                  {formatLastPlayed(entry.lastPlayedAt)}
                </div>
              </div>
            );
          })}
      </motion.div>

      {/* Back to Home */}
      <div className="mt-8 text-center">
        <Link
          to="/"
          className="font-pixel text-sm text-white/60 transition-colors hover:text-white"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
};
